import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, ShareNetwork, ArrowUpRight } from "@phosphor-icons/react";
import { api, imageSrc, CATEGORY_META, CATEGORY_GROUPS, findGroup } from "../lib/api";
import { toast } from "sonner";

/* Futuristic catalogue: horizontal snap cinema strip.
   Each slide is a magazine spread: image left, editorial text right, large index. */

export default function ProductShowcase({ products = [], activeGroup, onGroupChange }) {
  const stripRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const filtered = (() => {
    if (!activeGroup || activeGroup === "all") return products;
    const group = findGroup(activeGroup);
    if (!group) return products;
    return products.filter((p) => group.categories.includes(p.category));
  })();

  // Track which slide is centered
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onScroll = () => {
      const center = el.scrollLeft + el.clientWidth / 2;
      const slides = Array.from(el.querySelectorAll("[data-slide]"));
      let best = 0, bestD = Infinity;
      slides.forEach((s, i) => {
        const r = s.offsetLeft + s.clientWidth / 2;
        const d = Math.abs(center - r);
        if (d < bestD) { bestD = d; best = i; }
      });
      setActiveIdx(best);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [filtered.length]);

  // Reset on filter change
  useEffect(() => { stripRef.current?.scrollTo({ left: 0, behavior: "smooth" }); }, [activeGroup]);

  const scrollBy = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    const slide = el.querySelector("[data-slide]");
    const w = slide ? slide.clientWidth + 24 : 600;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  return (
    <section id="catalogue" className="relative py-20 md:py-28 overflow-hidden" data-testid="catalogue-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10 md:mb-14">
        <div>
          <span className="label-uppercase text-[#A78BFA]">02 · Catalogue</span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mt-3 leading-[1.05]">
            Issue Nº 01 <span className="font-serif italic font-normal text-slate-500">— scroll through</span>
          </h2>
        </div>

        <div className="flex flex-wrap gap-2 max-w-full" data-testid="catalogue-filters">
          <FilterChip active={!activeGroup || activeGroup === "all"} onClick={() => onGroupChange("all")} testid="filter-all">
            All
          </FilterChip>
          {CATEGORY_GROUPS.filter((g) => !g.comingSoon).map((g) => (
            <FilterChip
              key={g.slug}
              active={activeGroup === g.slug}
              onClick={() => onGroupChange(g.slug)}
              testid={`filter-${g.slug}`}
            >
              {g.name}
            </FilterChip>
          ))}
        </div>
      </div>

      <div
        ref={stripRef}
        className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pl-5 md:pl-8 pr-5 md:pr-8 pb-3 no-scrollbar"
        data-testid="catalogue-strip"
      >
        {filtered.length === 0 && (
          <div className="w-full text-center py-16 text-slate-500">No items in this category yet.</div>
        )}
        {filtered.map((p, idx) => (
          <CatalogueSlide key={p.id} product={p} index={idx + 1} total={filtered.length} />
        ))}
      </div>

      {/* progress + controls */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 mt-8 flex items-center gap-5">
        <span className="font-display text-xs tracking-[0.22em] text-slate-500">
          {String(activeIdx + 1).padStart(2, "0")} / {String(filtered.length || 0).padStart(2, "0")}
        </span>
        <div className="relative flex-1 h-px bg-slate-200" data-testid="catalogue-progress">
          <motion.div
            className="absolute inset-y-0 left-0 bg-[#0F172A]"
            style={{ width: filtered.length > 1 ? `${((activeIdx) / (filtered.length - 1)) * 100}%` : "0%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <div className="flex gap-2">
          <button onClick={() => scrollBy(-1)} data-testid="catalogue-prev" aria-label="Previous" className="w-10 h-10 rounded-full border border-slate-900 flex items-center justify-center hover:bg-slate-900 hover:text-white transition-colors">
            <ArrowUpRight size={14} weight="bold" className="-rotate-[135deg]" />
          </button>
          <button onClick={() => scrollBy(1)} data-testid="catalogue-next" aria-label="Next" className="w-10 h-10 rounded-full border border-slate-900 flex items-center justify-center hover:bg-slate-900 hover:text-white transition-colors">
            <ArrowUpRight size={14} weight="bold" className="rotate-45" />
          </button>
        </div>
      </div>
    </section>
  );
}

function FilterChip({ active, children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] tracking-[0.22em] uppercase font-medium px-3.5 py-2 rounded-full transition-all border ${
        active ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-600 hover:border-slate-900 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function CatalogueSlide({ product, index, total }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [-30, 30]);
  const [likes, setLikes] = useState(product.likes || 0);
  const [shares, setShares] = useState(product.shares || 0);
  const [liked, setLiked] = useState(false);

  const meta = CATEGORY_META[product.category] || { name: product.category, color: "from-pink-200 to-violet-200" };

  const onLike = async () => {
    if (liked) return;
    setLiked(true); setLikes((l) => l + 1);
    try { const r = await api.post(`/products/${product.id}/like`); setLikes(r.data.likes); } catch { /* noop */ }
  };
  const onShare = async () => {
    setShares((s) => s + 1);
    try { await api.post(`/products/${product.id}/share`); } catch { /* noop */ }
    const url = window.location.origin + `/#product-${product.id}`;
    try {
      if (navigator.share) await navigator.share({ title: product.name, text: product.tagline || product.description, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch { /* noop */ }
  };

  return (
    <article
      ref={ref}
      data-slide
      data-testid={`catalogue-slide-${product.id}`}
      id={`product-${product.id}`}
      className="shrink-0 snap-center w-[88vw] sm:w-[76vw] md:w-[62vw] lg:w-[54vw] xl:w-[48vw] max-w-[820px]"
    >
      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {/* IMAGE */}
        <div className="col-span-12 md:col-span-7 relative">
          <div className={`relative aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br ${meta.color}`}>
            {product.image_url && (
              <motion.img
                style={{ y: imageY }}
                src={imageSrc(product.image_url)}
                alt={product.name}
                draggable={false}
                className="absolute inset-[-8%] w-[116%] h-[116%] object-cover"
              />
            )}
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-3xl" />
            {/* corner index */}
            <div className="absolute top-4 left-4 font-display text-[11px] tracking-[0.28em] uppercase bg-white/95 backdrop-blur px-2.5 py-1 rounded-full">
              {String(index).padStart(2, "0")} · {meta.name || product.category}
            </div>
            {/* hover overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 flex items-end p-5 text-white">
              <p className="text-[13px] leading-relaxed line-clamp-3">{product.description}</p>
            </div>
          </div>
        </div>

        {/* TEXT */}
        <div className="col-span-12 md:col-span-5 flex flex-col justify-end">
          <div className="font-display text-[64px] md:text-[88px] font-light tracking-[-0.04em] leading-none text-slate-200">
            {String(index).padStart(2, "0")}
          </div>
          {product.tagline && <div className="mt-2 text-[12px] tracking-[0.22em] uppercase text-[#FF6B9E]">{product.tagline}</div>}
          <h3 className="mt-3 font-display text-2xl md:text-[28px] font-medium leading-[1.1] tracking-tight">
            {product.name}
          </h3>
          <p className="mt-3 text-[14px] text-slate-600 leading-relaxed line-clamp-4">
            {product.description}
          </p>
          <div className="mt-6 flex items-center justify-between pt-4 border-t border-slate-200">
            <div className="flex items-center gap-4 text-[11px] tracking-[0.22em] uppercase text-slate-500">
              <button onClick={onLike} data-testid={`like-${product.id}`} className="inline-flex items-center gap-1.5 hover:text-[#FF6B9E] transition-colors">
                <Heart size={13} weight={liked ? "fill" : "regular"} className={liked ? "text-[#FF6B9E]" : ""} /> {likes}
              </button>
              <button onClick={onShare} data-testid={`share-${product.id}`} className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors">
                <ShareNetwork size={13} weight="regular" /> {shares}
              </button>
            </div>
            <span className="text-[10px] tracking-[0.22em] uppercase text-slate-400">Nº {String(index).padStart(2, "0")}/{String(total).padStart(2, "0")}</span>
          </div>
        </div>
      </div>
    </article>
  );
}
